/**
 * TanStack-Query hook voor `/api/v1/tenant` — vervangt de Vue
 * `useTenantMetaStore` (Pinia). Het meta-blob bevat tenant-config
 * (locales, branding) plus org-levels + per-tenant user_field_schema
 * en wordt door zowat elke tenant-view aangeraakt; we cachen het
 * één keer en laten consumers via `useTenantMeta()` de selector
 * doen.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@shared/api/client';

export interface OrgLevelSummary {
  id: string;
  key: string;
  label_i18n: Record<string, string>;
  parent_level_id: string | null;
  position: number;
  /** Per-category membership rules — surfaced to the user-edit form. */
  membership_required: boolean;
  allow_multiple_units: boolean;
  field_schema: { fields: { key: string; type: string; required?: boolean; label?: Record<string, string> }[] };
  /**
   * i18n label override for the per-unit `external_id` field on units in
   * this category. `null` → use the SPA default ("Externe ID" / "External ID").
   */
  external_id_label: Record<string, string> | null;
}

/**
 * Logo + title arrangement in the sidebar brand block + login header.
 *  - `logo_only`         — show the logo, hide the portal-name text
 *  - `logo_then_title`   — logo on the left, portal-name beside it (default)
 *  - `logo_above_title`  — logo on top, portal-name underneath
 *  - `title_only`        — hide the logo, show just the portal-name
 */
export type BrandLogoLayout =
  | 'logo_only' | 'logo_then_title' | 'logo_above_title' | 'title_only';

/** Horizontal alignment of the brand block (logo + portal name together). */
export type BrandLogoAlign = 'left' | 'center';

export interface TenantBranding {
  primary_color?: string | null;
  accent_color?: string | null;
  button_hover_color?: string | null;
  active_indicator_color?: string | null;
  sidebar_color?: string | null;
  name_in_ui?: string | null;
  /**
   * De naam waaronder de opleidingsfunctie bij deze klant bekend staat — *HRM*, *HRD*,
   * *Opleidingen* (B36, issue 575). Ondertekent mail over leren als "HRD — {organisatie}";
   * onder account- en toegangsberichten staat alleen de organisatienaam.
   *
   * Staat bij de huisstijl omdat de superadmin hem daar zet, naast logo en kleur — niet omdat
   * de interface hem gebruikt. De schermen doen er niets mee.
   */
  sender_name?: string | null;
  logo_url?: string | null;
  favicon_url?: string | null;
  logo_layout?: BrandLogoLayout | null;
  logo_align?: BrandLogoAlign | null;
}

export interface TenantMeta {
  id: string;
  slug: string;
  name: string;
  /**
   * Welk oppervlak serveert deze host (ADR-0028): `manage` = de beheer-host,
   * `app` = de eindgebruiker-host.
   */
  surface: 'manage' | 'app';
  /** Bare hostname van de eindgebruiker-app (slug-subdomein of custom domein). */
  app_host: string;
  /** Bare hostname van de beheer-console (`manage-<slug>` of override). */
  manage_host: string;
  locale: string;
  available_locales: string[];
  available_locale_options: Array<{ code: string; label: string; position: number }>;
  /**
   * De contenttalen (punt 29): de talen van trainingen, hun materiaal en uitvoeringen, de standaard voorop. Een andere
   * lijst dan de interfacetalen hierboven; lees hem via `useContentLocales()`.
   */
  content_locales?: string[];
  default_content_locale?: string;
  branding: TenantBranding;
  /** Thema-beleid (issue #95): licht/donker afgedwongen, of gebruikerskeuze. */
  theme_mode: 'light' | 'dark' | 'switch';
  auth_driver: string;
  /**
   * Tenant-wide schema for the user-edit form's custom-attribute section, plus
   * per-tenant overrides on the always-present base fields (issue #140): label
   * per locale + required, keyed by the fixed base-field key.
   */
  user_field_schema: {
    fields: { key: string; type: string; required?: boolean; label?: Record<string, string> }[];
    base?: Record<string, { label?: Record<string, string>; required?: boolean }>;
  };
  /** i18n label override for `users.external_id`, or `null` for default. */
  user_external_id_label: Record<string, string> | null;
  org_levels: OrgLevelSummary[];
}

export const TENANT_META_QUERY_KEY = ['tenant', 'meta'] as const;

/**
 * Default-`enabled=true` zodat consumers in tenant-views (M9) niets
 * hoeven door te geven. App-shell zet `enabled=false` op de superadmin
 * shell zodat we niet elke 5 minuten een 404 op `/api/v1/tenant` halen.
 */
export function useTenantMeta(enabled: boolean = true) {
  return useQuery({
    queryKey: TENANT_META_QUERY_KEY,
    queryFn: async () => {
      const { data } = await api.get<{ data: TenantMeta }>('/api/v1/tenant');
      return data.data;
    },
    enabled,
    // Meta change rare; serve from cache aggressively.
    staleTime: 5 * 60 * 1000,
  });
}
